import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  ActionsDialogue, BarreOutils, Bouton, CaseACocher, Carte, Champ, Dialogue,
  EnTetePage, Espaceur, GroupeBoutons, IconeTelecharger, IconePlus,
  IconeRecherche, Message, Squelette, Vide, classeListeDialogue, useRetours,
} from '@journal/ui';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import {
  TableauJournal, domaineDeLaLigne,
  type ChampLigne, type SourceRecuperation,
} from '../components/TableauJournal.tsx';
import { lundiDe, type Progression } from './Progressions.tsx';
import { sansPrefixeNiveau } from '../components/SelecteurContenu.tsx';
import {
  lireSemainesPerso, semainesDePeriode, type Matiere, type Periode,
} from './Reglages.tsx';
import { useSession } from '../lib/session.tsx';
import { useAnneeActive } from '../lib/annee.tsx';
import { useTelechargementPdf } from '../lib/impression.tsx';
import { demandeV1 } from '../lib/impression.ts';
import { journalV1, titreV1 } from '../lib/gabarits-v1.ts';

/**
 * Cahier journal — une journée, en tableau.
 *
 * ── Aligné sur l'application d'origine ──────────────────────────────────────
 * Tout ce que la v1 affiche ET enregistre, et rien d'autre :
 *
 *   - la liste des PÉRIODES à gauche, chacune dépliable en ses semaines ;
 *   - « ← Préc. », la semaine en toutes lettres, « Suiv. → », « Aujourd'hui »,
 *     puis les onglets Lundi à Vendredi — on change de SEMAINE en gardant le
 *     jour choisi ;
 *   - dans la case Objectifs : les objectifs, le champ « Objectif libre… », et
 *     les deux boutons « Récupérer », qui ouvrent chacun SA fenêtre ;
 *   - la case « Duplication », la corbeille, « + Ajouter une ligne ».
 *
 * ── « Ajouter » insère une LIGNE, comme la v1 ───────────────────────────────
 * « Ajouter », en haut comme sous le tableau, ajoute une ligne vide en bas du
 * tableau, curseur dans l'heure de début. Elle a les mêmes champs, aux mêmes
 * places, et les mêmes gestes que les autres : objectif libre, les deux
 * « Récupérer », duplication, corbeille. Rien n'est écrit tant qu'on n'y a rien
 * saisi ; la séance naît à la première saisie, et la ligne reste à sa place.
 * (Une fenêtre de pré-saisie l'avait remplacée un temps ; on est revenu au
 * geste de la v1, à la demande.)
 *
 * ── La séance naît au premier geste ─────────────────────────────────────────
 * Un créneau de l'emploi du temps n'a pas de séance tant que rien n'y est
 * écrit (`_ensureEntry` de l'original). La progression prévue n'y est PAS
 * recopiée d'office : la v1 l'a retiré, une ligne de programme surgissant au
 * premier mot tapé sans que personne ne l'ait choisie. Elle s'affiche en
 * aperçu, « Planifié dans la progression », et un bouton l'ajoute.
 *
 * ── La date vit dans l'URL ──────────────────────────────────────────────────
 * `?date=2026-09-14` : un enseignant ouvre des onglets, revient en arrière,
 * partage un lien avec un remplaçant. Une date en mémoire n'y survit pas.
 */

export interface ObjectifSeance {
  id: string;
  text: string;
  procedure: string;
  done: boolean;
  position: number;
  curriculumObjectiveId: string | null;
  tenantObjectiveId: string | null;
}

export interface Seance {
  id: string;
  date: string;
  startsAt: string | null;
  endsAt: string | null;
  subjectId: string | null;
  subjectAreaId: string | null;
  subjectText: string;
  subjectAreaText: string;
  sequence: string;
  objective: string;
  procedure: string;
  materials: string;
  competencies: string;
  done: boolean;
  duplicated: boolean;
  duplicateOfId: string | null;
  objectives: ObjectifSeance[];
}

export interface LigneJour {
  /**
   * Repère d'une ligne ajoutée avec « Ajouter » : il la garde à sa place, et
   * le même élément à l'écran, quand la séance qu'elle a fait naître revient
   * du serveur. Sans lui, la ligne changeait d'identité à la première saisie,
   * et le curseur sortait du champ qu'on était en train de remplir.
   */
  cle?: string;
  slotId: string | null;
  /** Saisie libre du CRÉNEAU, reprise par le journal à défaut de séance. */
  slotSubjectText: string;
  slotSubjectAreaText: string;
  startsAt: string | null;
  endsAt: string | null;
  subjectId: string | null;
  subjectName: string;
  color: string;
  lesson: Seance | null;
  /** Progression prévue cette semaine pour cette matière — `progSugg` de l'original. */
  suggestion: {
    id: string;
    sequence: string;
    content: string;
    competencies: string;
    done: boolean;
  } | null;
}

interface ProgressionSemaine {
  id: string;
  subjectId: string;
  subjectName: string;
  curriculumDomainId: string | null;
  sequence: string;
  content: string;
  competencies: string;
  done: boolean;
}

interface VueJour {
  date: string;
  weekday: number;
  rows: LigneJour[];
  plannedInProgression: ProgressionSemaine[];
}

/** Un élément proposé par l'une des deux fenêtres « Récupérer ». */
export interface ElementRecuperable {
  cle: string;
  /** Matière et sous-domaine, en pastille — la progression seulement. */
  pastille?: string;
  /** « Bulle 2 », « Semaine du 21 septembre », « Lecture · notion »… */
  etiquette: string;
  titre: string;
  contenu: string;
}

/** Date du jour au format AAAA-MM-JJ, en heure locale. */
export function aujourdhuiIso(maintenant = new Date()): string {
  // `toISOString()` bascule en UTC : à 23 h à Paris en été, il renvoie le
  // lendemain. Un enseignant ouvrirait le cahier sur le mauvais jour.
  const decalage = maintenant.getTimezoneOffset() * 60_000;
  return new Date(maintenant.getTime() - decalage).toISOString().slice(0, 10);
}

/** Décale une date ISO d'un nombre de jours, sans passer par un fuseau. */
export function decalerJour(iso: string, jours: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + jours);
  return date.toISOString().slice(0, 10);
}

/** Saut au jour ouvré suivant ou précédent : le week-end n'a pas de classe. */
export function jourOuvreSuivant(iso: string, sens: 1 | -1): string {
  let courant = decalerJour(iso, sens);
  while ([0, 6].includes(new Date(`${courant}T00:00:00Z`).getUTCDay())) {
    courant = decalerJour(courant, sens);
  }
  return courant;
}

/**
 * Rang du jour dans la semaine de classe : 0 = lundi … 4 = vendredi.
 *
 * Le week-end retombe sur le lundi, comme l'ouverture de l'original : il n'y a
 * pas d'onglet samedi.
 */
export function rangDuJour(iso: string): number {
  const jour = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return jour >= 1 && jour <= 5 ? jour - 1 : 0;
}

export const dateLongue = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });

const formater = (iso: string, options: Intl.DateTimeFormatOptions) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', { ...options, timeZone: 'UTC' });

/** « Semaine du 14 septembre au 18 septembre 2026 » — le libellé de l'original. */
export function libelleSemaine(lundi: string): string {
  return `Semaine du ${formater(lundi, { day: 'numeric', month: 'long' })} au `
    + `${formater(decalerJour(lundi, 4), { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

const JOURS_OUVRES = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];

/** Insensible à la casse ET aux accents : « Francais » trouve « Français ». */
const normaliser = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

/**
 * La matière correspond-elle au domaine saisi sur la ligne ?
 *
 * La règle de l'original, dans les deux sens : « Maths » trouve
 * « Mathématiques », et « Français — lecture » trouve « Français ».
 */
export function memeMatiere(matiere: string, domaine: string): boolean {
  const m = normaliser(matiere);
  const d = normaliser(domaine);
  return !!m && (m === d || m.includes(d) || d.includes(m));
}

/**
 * Les éléments de « Récupérer depuis la progression ».
 *
 * Reprend `_openObjectifPicker` de l'original : une entrée par DÉTAIL de
 * séance (le titre de la séance et ce détail), ou le titre seul quand la
 * séance n'a pas de détail. Filtrage STRICT sur le domaine de la ligne : une
 * ligne « Mathématiques » ne propose jamais une séance de « Français ».
 */
export function elementsDeProgression(
  progressions: Array<{
    id: string; matiere: string; sousDomaine: string;
    sequence: string; content: string; competencies: string;
  }>,
  domaine: string,
  etiquetteSemaine = '',
): ElementRecuperable[] {
  const vus = new Set<string>();
  const liste: ElementRecuperable[] = [];
  for (const p of progressions) {
    if (domaine.trim() && !memeMatiere(p.matiere, domaine)) continue;
    const titre = (p.sequence || p.content).trim();
    const bulles = p.competencies.split('\n').map((b) => b.trim()).filter(Boolean);
    if (!titre && bulles.length === 0) continue;
    const pastille = [p.matiere, p.sousDomaine].filter(Boolean).join(' · ');
    const entrees = bulles.length === 0
      ? [{ contenu: '', etiquette: etiquetteSemaine }]
      : bulles.map((b, i) => ({
        contenu: `${i + 1}. ${b}`,
        etiquette: [etiquetteSemaine, `Bulle ${i + 1}`].filter(Boolean).join(' · '),
      }));
    for (const [i, e] of entrees.entries()) {
      const cle = `${p.matiere}|${titre}|${e.contenu}`;
      if (vus.has(cle)) continue;
      vus.add(cle);
      liste.push({ cle: `${p.id}-${i}`, pastille, etiquette: e.etiquette, titre, contenu: e.contenu });
    }
  }
  return liste;
}

interface Recuperation {
  /** La ligne qui recevra l'élément, ou la fenêtre « Ajouter » en cours de saisie. */
  ligne: LigneJour;
  source: SourceRecuperation;
  titre: string;
  elements: ElementRecuperable[];
}

export function Journal() {
  const { api } = useSession();
  const annee = useAnneeActive();
  const [parametres, setParametres] = useSearchParams();
  const { notifier, confirmer } = useRetours();

  /*
   * Sans date dans l'adresse : aujourd'hui, et le LUNDI si l'on est le
   * week-end — l'original ouvre sur le premier onglet de la semaine en cours.
   */
  const date = parametres.get('date') ?? (() => {
    const auj = aujourdhuiIso();
    return decalerJour(lundiDe(auj), rangDuJour(auj));
  })();
  const lundi = lundiDe(date);
  const rang = rangDuJour(date);

  /*
   * Le programme de la CLASSE, toutes versions fusionnées.
   *
   * Il donne les propositions des colonnes Domaine et Sous-domaine, et le
   * contenu de « Récupérer depuis le programme de l'année ».
   */
  const arbre = useDonnees<Array<{
    id: string;
    name: string;
    subject: string;
    subdomains: Array<{ name: string; objectives: Array<{ id: string; text: string }> }>;
  }>>('/curriculum/tree');

  /*
   * Les matières de l'école s'ajoutent à celles du programme, comme dans
   * l'original : une matière créée par l'école — « Anglais », « Chorale » —
   * n'était sinon jamais proposée.
   */
  const matieresEcole = useDonnees<Matiere[]>('/subjects');
  const domainesProposes = useMemo(
    () => [...new Set([
      ...(arbre.donnees ?? []).map((d) => d.subject),
      ...(matieresEcole.donnees ?? []).map((m) => m.name),
    ].map((n) => (n ?? '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'fr')),
    [arbre.donnees, matieresEcole.donnees],
  );
  const sousDomainesProposes = useMemo(() => {
    const parMatiere: Record<string, string[]> = {};
    for (const d of arbre.donnees ?? []) {
      if (!d.subject) continue;
      const nom = sansPrefixeNiveau(d.name);
      const liste = parMatiere[d.subject] ?? [];
      if (!liste.includes(nom)) liste.push(nom);
      parMatiere[d.subject] = liste;
    }
    for (const liste of Object.values(parMatiere)) liste.sort((a, b) => a.localeCompare(b, 'fr'));
    return parMatiere;
  }, [arbre.donnees]);

  const jour = useDonnees<VueJour>(`/lessons/day?date=${date}`, [date]);

  /*
   * ── Une seule création par ligne, même en tapant vite ──────────────────
   * Une ligne de l'emploi du temps n'a pas encore de séance : la première
   * saisie la crée. Taper le domaine puis aussitôt le déroulement lançait
   * DEUX créations — la seconde partait avant que la réponse de la première
   * n'arrive — et l'une des saisies finissait dans une séance en double,
   * invisible sous la ligne. La création en cours est donc partagée : les
   * saisies suivantes l'attendent, puis complètent la même séance.
   *
   * Une entrée tombe quand la journée rechargée porte la séance : la ligne a
   * alors son `lesson`, et une séance supprimée plus tard ne serait pas
   * ressuscitée par une création périmée.
   */
  const creations = useRef(new Map<string, {
    promesse: Promise<{ seance: Seance; objectifsRecopies: number } | null>;
    resolue: boolean;
  }>());
  useEffect(() => {
    for (const [cle, c] of creations.current) if (c.resolue) creations.current.delete(cle);
  }, [jour.donnees]);
  const periodes = useDonnees<Periode[]>('/terms');
  const reglages = useDonnees<Record<string, string>>('/settings');

  /*
   * Calendrier officiel des vacances : il marque la journée « 🌴 Vacances »
   * et grise les semaines chômées de la liste des périodes.
   */
  const anneeRentree = useMemo(() => {
    const d = new Date(`${date}T00:00:00Z`);
    return d.getUTCMonth() >= 7 ? d.getUTCFullYear() : d.getUTCFullYear() - 1;
  }, [date]);
  const conges = useDonnees<{
    zone: string;
    holidays: Array<{ nom: string; debut: string; fin: string }>;
  }>(
    `/settings/holidays/${anneeRentree}?zone=${reglages.donnees?.zone ?? 'B'}`,
    [anneeRentree, reglages.donnees?.zone],
  );
  const vacances = conges.donnees?.holidays ?? [];
  const enVacances = vacances.some((v) => v.debut <= date && date <= v.fin);

  const { executer, erreur: erreurEcriture } = useEcriture();
  const pdf = useTelechargementPdf();

  const [recuperation, setRecuperation] = useState<Recuperation | null>(null);
  /** Décocher « Duplication » avec des copies retouchées : la fenêtre de choix. */
  const [annulation, setAnnulation] = useState<{ seanceId: string; copies: CopieRetouchee[] } | null>(null);
  /*
   * ── Les lignes ajoutées avec « Ajouter » ──────────────────────────────────
   * Une ligne vide n'existe que sur l'écran. Quand une saisie y fait naître
   * une séance, `seanceId` la rattache : la séance revenue du serveur prend
   * alors la place de la ligne, avec le même repère, au même rang.
   * Changer de jour les oublie : celles restées vides n'avaient rien à garder.
   */
  const [ajoutees, setAjoutees] = useState<Array<{ cle: string; seanceId: string | null }>>([]);
  const [aFocaliser, setAFocaliser] = useState<string | null>(null);
  const compteurLignes = useRef(0);
  useEffect(() => { setAjoutees([]); }, [date]);
  useEffect(() => {
    if (!aFocaliser) return;
    // Le curseur dans l'heure de début, comme `addRow` de l'original.
    document.querySelector<HTMLInputElement>(`tr[data-cle="${aFocaliser}"] input`)?.focus();
    setAFocaliser(null);
  }, [aFocaliser]);

  function ajouterLigne() {
    compteurLignes.current += 1;
    const cle = `ajoutee-${compteurLignes.current}`;
    setAjoutees((l) => [...l, { cle, seanceId: null }]);
    setAFocaliser(cle);
  }

  function rattacher(cle: string, seanceId: string) {
    setAjoutees((l) => l.map((a) => (a.cle === cle ? { ...a, seanceId } : a)));
  }

  function allerA(nouvelle: string) {
    // Chaque journée consultée entre dans l'historique : Retour y revient.
    setParametres({ date: nouvelle });
  }

  /** La semaine qui commence ce lundi, au même jour que celui affiché. */
  function allerSemaine(lundiCible: string) {
    allerA(decalerJour(lundiCible, rang));
  }

  /**
   * La progression prévue, recopiée comme premier objectif d'une séance neuve.
   *
   * Le texte de l'original : les détails de la séance, à défaut son titre, à
   * défaut son contenu.
   */
  async function recopierPrevu(ligne: LigneJour, seanceId: string): Promise<number> {
    const prevu = ligne.suggestion;
    if (!prevu || !(prevu.sequence || prevu.content)) return 0;
    const texte = (prevu.competencies || prevu.sequence || prevu.content).trim().slice(0, 2000);
    if (!texte) return 0;
    await api.requete(`/lessons/${seanceId}/objectives`, { methode: 'POST', corps: { text: texte } });
    return 1;
  }

  /**
   * La séance de cette ligne, créée si elle n'existe pas encore.
   *
   * `avecPrevu` : la progression de la semaine est recopiée à la création —
   * sauf quand on ajoute soi-même un objectif.
   */
  async function assurerSeance(
    ligne: LigneJour,
    avecPrevu: boolean,
    champs: Partial<Record<ChampLigne, string>> = {},
  ): Promise<{ seance: Seance; objectifsRecopies: number } | null> {
    if (ligne.lesson) return { seance: ligne.lesson, objectifsRecopies: 0 };

    const cle = `${date}|${ligne.cle ?? ligne.slotId ?? `${ligne.startsAt ?? ''}-${ligne.subjectId ?? ''}`}`;
    const enCours = creations.current.get(cle);
    if (enCours) {
      const creee = await enCours.promesse;
      if (!creee) return null;
      // La séance existe déjà : cette saisie la COMPLÈTE au lieu d'en créer une autre.
      if (Object.keys(champs).length > 0) {
        try {
          await api.requete(`/lessons/${creee.seance.id}`, { methode: 'PATCH', corps: champs });
        } catch {
          return null;
        }
      }
      return { seance: creee.seance, objectifsRecopies: 0 };
    }

    const entree = { promesse: creer(), resolue: false };
    creations.current.set(cle, entree);
    const creee = await entree.promesse;
    entree.resolue = true;
    if (!creee) creations.current.delete(cle);
    return creee;

    async function creer(): Promise<{ seance: Seance; objectifsRecopies: number } | null> {
      try {
        const seance = await api.requete<Seance>('/lessons', {
          methode: 'POST',
          corps: {
            date,
            startsAt: ligne.startsAt,
            endsAt: ligne.endsAt,
            subjectId: ligne.subjectId,
            /*
             * Le LIEN vers le créneau, et non le seul horaire : décaler la séance
             * d'un quart d'heure ne la détache plus de sa ligne.
             */
            timetableSlotId: ligne.slotId,
            ...champs,
          },
        });
        if (ligne.cle) rattacher(ligne.cle, seance.id);
        const objectifsRecopies = avecPrevu ? await recopierPrevu(ligne, seance.id) : 0;
        return { seance, objectifsRecopies };
      } catch {
        return null;
      }
    }
  }

  /**
   * Enregistre un champ saisi dans la ligne.
   *
   * Renvoie `false` en cas d'échec pour que le champ garde le texte à l'écran.
   */
  async function enregistrerChamp(
    ligne: LigneJour,
    champ: ChampLigne,
    valeur: string,
  ): Promise<boolean> {
    /*
     * Une heure VIDÉE vaut `null` : le serveur attend `HH:MM` ou rien, et
     * refuserait la chaîne vide sans qu'aucun message ne le dise.
     */
    const estHeure = champ === 'startsAt' || champ === 'endsAt';
    const aEnvoyer: string | null = estHeure && valeur === '' ? null : valeur;

    try {
      if (ligne.lesson) {
        await api.requete(`/lessons/${ligne.lesson.id}`, {
          methode: 'PATCH',
          corps: { [champ]: aEnvoyer },
        });
      } else {
        // Rien à créer pour un champ qu'on vient de vider.
        if (valeur.trim() === '') return true;
        if (!(await assurerSeance(ligne, false, { [champ]: valeur }))) return false;
      }
      await jour.recharger();
      return true;
    } catch {
      return false;
    }
  }

  /** Ajoute un objectif à la séance de la ligne, créée si besoin. */
  async function ajouterObjectif(ligne: LigneJour, texte: string): Promise<boolean> {
    const ok = await executer(async () => {
      const creee = await assurerSeance(ligne, false);
      if (!creee) throw new Error('La séance n’a pas pu être créée.');
      await api.requete(`/lessons/${creee.seance.id}/objectives`, {
        methode: 'POST',
        corps: { text: texte.slice(0, 2000) },
      });
    });
    await jour.recharger();
    return ok;
  }

  async function modifierObjectif(objectifId: string, texte: string): Promise<boolean> {
    try {
      await api.requete(`/lessons/objectives/${objectifId}`, {
        methode: 'PATCH', corps: { text: texte },
      });
      await jour.recharger();
      return true;
    } catch { return false; }
  }

  async function supprimerObjectif(objectifId: string) {
    const ok = await executer(() =>
      api.requete(`/lessons/objectives/${objectifId}`, { methode: 'DELETE' }));
    if (ok) await jour.recharger();
  }

  /**
   * La case « Duplication » : cocher recopie, décocher retire les copies.
   *
   * Décocher, comme dans l'original : les copies des semaines à venir que
   * l'enseignant a retouchées depuis lui sont MONTRÉES, et il coche celles
   * qu'il veut garder. Sans copie retouchée, rien n'est demandé.
   */
  async function basculerDuplication(ligne: LigneJour, coche: boolean) {
    if (!coche) {
      if (!ligne.lesson) return;
      const seanceId = ligne.lesson.id;
      let retouchees: CopieRetouchee[] = [];
      const lu = await executer(async () => {
        retouchees = (await api.requete<{ modified: CopieRetouchee[] }>(
          `/lessons/${seanceId}/duplicate/futures`,
        )).modified;
      });
      if (!lu) return;
      if (retouchees.length > 0) {
        // La case reste cochée tant que la fenêtre n'est pas confirmée.
        setAnnulation({ seanceId, copies: retouchees });
        return;
      }
      await annulerDuplication(seanceId, []);
      return;
    }

    await executer(async () => {
      const creee = await assurerSeance(ligne, false);
      if (!creee) throw new Error('La séance n’a pas pu être créée.');
      const { seance } = creee;
      const vide = seance.objectives.length === 0
        && !seance.procedure.trim() && !seance.materials.trim();
      if (vide) {
        notifier('Séance vide : remplissez-la (objectif, déroulement ou matériel) puis recochez.', 'info');
        return;
      }
      await dupliquer(seance.id);
    });
    await jour.recharger();
  }

  /** Retire les copies futures, sauf celles désignées à garder. */
  async function annulerDuplication(seanceId: string, garder: string[]): Promise<boolean> {
    const ok = await executer(async () => {
      const r = await api.requete<{ removed: number; kept: number }>(
        `/lessons/${seanceId}/duplicate/cancel`, { methode: 'POST', corps: { keep: garder } },
      );
      notifier(r.kept > 0
        ? `Duplication annulée : ${r.kept} gardée${r.kept > 1 ? 's' : ''}, `
          + `${r.removed} supprimée${r.removed > 1 ? 's' : ''}.`
        : `Duplication annulée pour ${r.removed} semaine(s) future(s)`);
    });
    await jour.recharger();
    return ok;
  }

  /**
   * Recopie une séance sur les semaines suivantes, et dit ce qui s'est passé.
   *
   * Rien de recopié : le serveur ne coche pas la case, et l'on explique
   * pourquoi — les raisons de l'original, plutôt qu'un « rien à recopier ».
   */
  async function dupliquer(seanceId: string) {
    const r = await api.requete<{
      created: number; filled: number; skipped: number; yearEnd: string;
      reason: 'hors_annee' | 'deja_remplies' | 'aucune_semaine' | null;
    }>(`/lessons/${seanceId}/duplicate`, { methode: 'POST' });
    const semaines = r.created + r.filled;
    if (semaines > 0) {
      notifier(`Séance recopiée sur ${semaines} semaine(s) suivante(s)`);
      return;
    }
    const raisons = {
      hors_annee: 'Aucune semaine de classe après cette séance : la dernière enregistrée est le '
        + `${formater(r.yearEnd, { day: 'numeric', month: 'long', year: 'numeric' })}. `
        + 'Vérifiez les dates des périodes dans les Réglages.',
      deja_remplies: `Les ${r.skipped} semaine(s) suivante(s) contiennent déjà une séance à cet `
        + 'horaire : elles ne sont jamais écrasées.',
      aucune_semaine: 'Aucune semaine suivante ne tombe dans une période de l’année scolaire.',
    };
    notifier(r.reason ? raisons[r.reason] : 'Aucune semaine à recopier.', 'info');
  }

  /**
   * « Ajouter cette séance au journal » : la progression prévue devient le
   * premier objectif. C'est le seul geste qui la recopie.
   */
  async function ajouterPrevu(ligne: LigneJour) {
    const ok = await executer(async () => {
      if (ligne.lesson) {
        // La séance existe déjà (un déroulement saisi) : on y ajoute le prévu.
        if (!(await recopierPrevu(ligne, ligne.lesson.id))) throw new Error('Rien de prévu à ajouter.');
        return;
      }
      if (!(await assurerSeance(ligne, true))) throw new Error('La séance n’a pas pu être créée.');
    });
    if (ok) notifier('Séance ajoutée au journal depuis la progression');
    await jour.recharger();
  }

  /**
   * La corbeille de la ligne, comme `_deleteRow` de l'original.
   *
   * Une ligne de l'emploi du temps réapparaîtrait à chaque ouverture si l'on
   * n'effaçait que la séance : la supprimer retire donc aussi le créneau, après
   * l'avoir dit. Une ligne ajoutée et jamais remplie disparaît simplement.
   */
  async function supprimerLigne(ligne: LigneJour) {
    // Ajoutée et jamais remplie : elle disparaît, sans question.
    if (ligne.cle && !ligne.lesson) {
      setAjoutees((l) => l.filter((a) => a.cle !== ligne.cle));
      return;
    }
    if (ligne.slotId) {
      const oui = await confirmer({
        titre: 'Supprimer cette séance ?',
        message: 'Cette séance provient de l’emploi du temps. La supprimer retirera '
          + 'aussi le créneau correspondant : il ne réapparaîtra plus dans le journal.',
        libelleConfirmation: 'Supprimer',
        destructeur: true,
      });
      if (!oui) return;
      const slotId = ligne.slotId;
      const ok = await executer(async () => {
        if (ligne.lesson) await api.requete(`/lessons/${ligne.lesson.id}`, { methode: 'DELETE' });
        await api.requete(`/timetable/${slotId}`, { methode: 'DELETE' });
      });
      if (ok) {
        notifier('Séance et créneau supprimés');
        await jour.recharger();
      }
      return;
    }
    if (ligne.lesson) {
      const oui = await confirmer({
        titre: 'Supprimer cette séance ?',
        message: 'Son déroulement, ses objectifs et son matériel seront perdus.',
        libelleConfirmation: 'Supprimer',
        destructeur: true,
      });
      if (!oui) return;
      const seanceId = ligne.lesson.id;
      const ok = await executer(() => api.requete(`/lessons/${seanceId}`, { methode: 'DELETE' }));
      if (ok) {
        if (ligne.cle) setAjoutees((l) => l.filter((a) => a.cle !== ligne.cle));
        notifier('Séance supprimée');
        await jour.recharger();
      }
    }
  }

  /** Nom d'un domaine du programme, pour la pastille d'une séance de progression. */
  const nomDomaine = (id: string | null) =>
    sansPrefixeNiveau((arbre.donnees ?? []).find((d) => d.id === id)?.name ?? '');

  /** « Récupérer depuis la progression » : la semaine, puis les autres semaines. */
  async function ouvrirProgression(ligne: LigneJour) {
    const domaine = domaineDeLaLigne(ligne);
    let elements = elementsDeProgression(
      (jour.donnees?.plannedInProgression ?? []).map((p) => ({
        id: p.id, matiere: p.subjectName, sousDomaine: nomDomaine(p.curriculumDomainId),
        sequence: p.sequence, content: p.content, competencies: p.competencies,
      })),
      domaine,
    );

    /*
     * Rien cette semaine : les séances du MÊME domaine prévues sur d'autres
     * semaines, étiquetées par leur date — cas fréquent quand on ouvre le
     * journal d'une semaine avant d'avoir rempli sa progression.
     */
    if (elements.length === 0 && domaine.trim()) {
      const [toutes, matieres] = await Promise.all([
        api.requete<Progression[]>('/progressions').catch(() => [] as Progression[]),
        api.requete<Matiere[]>('/subjects').catch(() => [] as Matiere[]),
      ]);
      const nomMatiere = new Map(matieres.map((m) => [m.id, m.name]));
      for (const p of toutes) {
        if (p.weekStart === lundi) continue;
        elements.push(...elementsDeProgression(
          [{
            id: p.id, matiere: nomMatiere.get(p.subjectId) ?? '',
            sousDomaine: nomDomaine(p.curriculumDomainId ?? null),
            sequence: p.sequence, content: p.content, competencies: p.competencies,
          }],
          domaine,
          `Semaine du ${formater(p.weekStart, { day: 'numeric', month: 'long' })}`,
        ));
      }
      elements = elements.filter((e, i, l) => l.findIndex((x) => x.cle === e.cle) === i);
    }

    if (elements.length === 0) {
      const quand = formater(lundi, { day: 'numeric', month: 'long', year: 'numeric' });
      notifier(domaine.trim()
        ? `Aucune séance de « ${domaine} » dans la progression (ni semaine du ${quand}, ni ailleurs).`
        : `Aucune séance dans la progression pour la semaine du ${quand}.`, 'info');
      return;
    }
    setRecuperation({ ligne, source: 'progression', titre: 'Récupérer depuis la progression', elements });
  }

  /** « Récupérer depuis le programme de l'année » : le domaine de la ligne d'abord. */
  function ouvrirProgramme(ligne: LigneJour) {
    const domaine = domaineDeLaLigne(ligne).trim();
    if (!domaine) {
      notifier('Choisissez d’abord un domaine sur la ligne.', 'info');
      return;
    }
    const elements: ElementRecuperable[] = [];
    for (const d of arbre.donnees ?? []) {
      // `LIKE %domaine%` de l'original : la matière CONTIENT ce qui est saisi.
      if (!normaliser(d.subject).includes(normaliser(domaine))) continue;
      const nomD = sansPrefixeNiveau(d.name);
      for (const sd of d.subdomains) {
        const notion = sansPrefixeNiveau(sd.name);
        elements.push({ cle: `${d.id}-${sd.name}`, etiquette: `${nomD} · notion`, titre: '', contenu: notion });
        for (const o of sd.objectives) {
          elements.push({ cle: o.id, etiquette: nomD, titre: notion, contenu: o.text });
        }
      }
    }
    if (elements.length === 0) {
      notifier(`Aucun contenu du programme pour « ${domaine} ».`, 'info');
      return;
    }
    setRecuperation({
      ligne, source: 'programme', titre: `Programme de l’année · ${domaine}`, elements,
    });
  }

  async function recupererElement(element: ElementRecuperable) {
    if (!recuperation) return;
    // Titre et contenu réunis dans UN objectif, modifiable ensuite.
    const texte = [element.titre, element.contenu].map((t) => t.trim()).filter(Boolean).join('\n');
    const ligne = recuperation.ligne;
    setRecuperation(null);
    if (texte) await ajouterObjectif(ligne, texte);
  }

  if (annee.erreur) {
    return (
      <Carte titre="Cahier journal">
        <Vide titre="Aucune année scolaire active">
          Le cahier journal suit l'emploi du temps de l'année en cours.
          Créez une année scolaire dans les réglages pour commencer.
        </Vide>
      </Carte>
    );
  }

  if (jour.chargement && !jour.donnees) {
    return (
      <div className="flex flex-col gap-5">
        <EnTetePage titre="Cahier journal" />
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => <Squelette key={i} className="h-24 rounded-xl" />)}
        </div>
      </div>
    );
  }
  if (jour.erreur) return <Message ton="erreur">{jour.erreur}</Message>;

  /*
   * Les lignes du serveur, puis les lignes ajoutées, dans l'ordre d'ajout. Une
   * ligne ajoutée dont la séance est revenue est remplacée par celle-ci, mais
   * garde son repère et son rang : elle ne saute pas ailleurs dans le tableau
   * pendant qu'on la remplit.
   */
  const lignesServeur = jour.donnees?.rows ?? [];
  const parSeance = new Map(ajoutees.filter((a) => a.seanceId).map((a) => [a.seanceId!, a.cle]));
  const lignes: LigneJour[] = [
    ...lignesServeur.filter((l) => !(l.lesson && parSeance.has(l.lesson.id))),
    ...ajoutees.map((a): LigneJour => {
      const revenue = a.seanceId ? lignesServeur.find((l) => l.lesson?.id === a.seanceId) : undefined;
      return revenue ? { ...revenue, cle: a.cle } : {
        cle: a.cle, slotId: null, slotSubjectText: '', slotSubjectAreaText: '',
        startsAt: null, endsAt: null, subjectId: null, subjectName: '',
        color: '#94a3b8', lesson: null, suggestion: null,
      };
    }),
  ];

  /*
   * Le PDF de la journée, au modèle de la v1 (`lib/gabarits-v1.ts`) : A4
   * portrait, l'essentiel de la largeur au déroulement, la couleur de la
   * matière en bande.
   */
  const impression = {
    enCours: pdf.enCours,
    erreur: pdf.erreur,
    lancer: () => {
      const jourLong = dateLongue(date);
      const titre = titreV1(jourLong.charAt(0).toUpperCase() + jourLong.slice(1), annee.donnees?.label);
      void pdf.telecharger(demandeV1(journalV1(titre, lignes.map((l) => ({
        debut: l.lesson?.startsAt ?? l.startsAt,
        fin: l.lesson?.endsAt ?? l.endsAt,
        domaine: domaineDeLaLigne(l),
        sousDomaine: l.lesson?.subjectAreaText || l.slotSubjectAreaText,
        couleur: l.color,
        objectifs: (l.lesson?.objectives ?? []).map((o) => ({
          texte: o.text,
          fait: 'done' in o ? Boolean((o as { done?: boolean }).done) : false,
        })),
        deroulement: l.lesson?.procedure ?? '',
        materiel: l.lesson?.materials ?? '',
      })))));
    },
  };

  return (
    <div className="flex flex-col gap-4">
      <EnTetePage titre="Cahier journal">
        <BarreOutils>
          <Bouton variante="secondary" taille="sm" onClick={() => allerSemaine(decalerJour(lundi, -7))}>
            ← Préc.
          </Bouton>
          <span className="text-[13px] font-medium">{libelleSemaine(lundi)}</span>
          <Bouton variante="secondary" taille="sm" onClick={() => allerSemaine(decalerJour(lundi, 7))}>
            Suiv. →
          </Bouton>
          <Bouton
            variante="secondary"
            taille="sm"
            onClick={() => allerSemaine(lundiDe(aujourdhuiIso()))}
          >
            Aujourd'hui
          </Bouton>

          <Espaceur />

          <GroupeBoutons
            legende="Jour de la semaine"
            valeur={date}
            surChangement={(nouvelle) => allerA(nouvelle)}
            options={JOURS_OUVRES.map((nom, index) => ({
              valeur: decalerJour(lundi, index),
              texte: nom,
            }))}
          />
          <Bouton
            variante="ghost"
            taille="sm"
            icone={<IconeTelecharger taille={15} />}
            chargement={impression.enCours}
            onClick={impression.lancer}
          >
            Télécharger le PDF
          </Bouton>
          <Bouton variante="primary" taille="sm" icone={<IconePlus taille={15} />} onClick={ajouterLigne}>
            Ajouter
          </Bouton>
        </BarreOutils>
      </EnTetePage>

      {impression.erreur && <Message ton="erreur">{impression.erreur}</Message>}
      {erreurEcriture && <Message ton="erreur">{erreurEcriture}</Message>}

      <div className="grid items-start gap-4 lg:grid-cols-[11rem_minmax(0,1fr)]">
        <ListePeriodes
          periodes={periodes.donnees ?? []}
          semainesPerso={lireSemainesPerso(reglages.donnees?.semaines_perso)}
          vacances={vacances}
          lundiActif={lundi}
          surSemaine={allerSemaine}
        />

        <Carte sansRembourrage>
          <div className="flex items-center gap-2.5 border-b border-[var(--color-border)] px-3.5 py-2.5">
            <span className="font-semibold capitalize">
              {formater(date, { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
            {enVacances && (
              <span className="rounded-full bg-[var(--color-surface-raised)] px-2 py-0.5 text-[12px]">
                🌴 Vacances
              </span>
            )}
          </div>

          <TableauJournal
            lignes={lignes}
            surChamp={enregistrerChamp}
            surObjectif={modifierObjectif}
            surSuppressionObjectif={(o) => void supprimerObjectif(o.id)}
            surObjectifLibre={ajouterObjectif}
            surRecuperation={(ligne, source) => void (source === 'progression'
              ? ouvrirProgression(ligne)
              : ouvrirProgramme(ligne))}
            surAjoutPrevu={(ligne) => void ajouterPrevu(ligne)}
            surDuplication={(ligne, coche) => void basculerDuplication(ligne, coche)}
            surSuppression={(ligne) => void supprimerLigne(ligne)}
            surAjoutLigne={ajouterLigne}
            domaines={domainesProposes}
            sousDomaines={sousDomainesProposes}
          />
        </Carte>
      </div>

      <DialogueAnnulationDuplication
        annulation={annulation}
        surFermeture={() => setAnnulation(null)}
        surConfirmation={async (garder) => {
          if (!annulation) return;
          if (await annulerDuplication(annulation.seanceId, garder)) setAnnulation(null);
        }}
      />

      <DialogueRecuperation
        recuperation={recuperation}
        surFermeture={() => setRecuperation(null)}
        surChoix={(e) => void recupererElement(e)}
      />
    </div>
  );
}

/**
 * La liste des périodes, dépliables en semaines — la barre de gauche de
 * l'original.
 *
 * La période en cours est dépliée à l'ouverture. Une semaine de vacances est
 * signalée ; la semaine affichée est surlignée.
 */
function ListePeriodes({
  periodes, semainesPerso, vacances, lundiActif, surSemaine,
}: {
  periodes: Periode[];
  semainesPerso: Record<string, string[]> | null;
  vacances: Array<{ debut: string; fin: string }>;
  lundiActif: string;
  surSemaine: (lundi: string) => void;
}) {
  const auj = aujourdhuiIso();
  const [ouvertes, setOuvertes] = useState<Set<string> | null>(null);
  // Tant que l'enseignant n'a rien replié ni déplié : la période en cours.
  const courante = periodes.find((p) => p.startsOn <= auj && auj <= p.endsOn)?.id;
  const deplies = ouvertes ?? new Set(courante ? [courante] : []);

  function basculer(id: string) {
    const suite = new Set(deplies);
    if (suite.has(id)) suite.delete(id); else suite.add(id);
    setOuvertes(suite);
  }

  return (
    <nav
      aria-label="Périodes et semaines"
      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-2 shadow-[var(--ombre-carte)] print:hidden"
    >
      <p className="px-1.5 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
        Périodes
      </p>
      <ul className="flex flex-col gap-0.5">
        {periodes.map((p) => {
          const deplie = deplies.has(p.id);
          const semaines = deplie ? semainesDePeriode(p, semainesPerso) : [];
          return (
            <li key={p.id}>
              <button
                type="button"
                aria-expanded={deplie}
                onClick={() => basculer(p.id)}
                className={[
                  'w-full rounded-md px-1.5 py-1 text-left text-[13px] font-medium',
                  'hover:bg-[var(--color-surface-hover)]',
                  p.id === courante ? 'text-[var(--color-primary)]' : '',
                ].join(' ')}
              >
                {p.name}
              </button>
              {deplie && (
                <ul className="mb-1 flex flex-col">
                  {semaines.map((l, i) => {
                    const lundi = lundiDe(l);
                    const enVac = vacances.some((v) => l >= v.debut && l <= v.fin);
                    const active = lundi === lundiActif;
                    return (
                      <li key={l}>
                        <button
                          type="button"
                          onClick={() => surSemaine(lundi)}
                          aria-current={active ? 'true' : undefined}
                          title={enVac ? '🌴 Vacances' : undefined}
                          className={[
                            'flex w-full items-center justify-between rounded-md px-2 py-0.5 text-[12px]',
                            active
                              ? 'bg-[var(--color-primary)] text-[var(--color-primary-fg)]'
                              : 'hover:bg-[var(--color-surface-hover)]',
                            enVac && !active ? 'text-[var(--color-fg-muted)] line-through' : '',
                          ].join(' ')}
                        >
                          <span className="font-semibold">{`S${i + 1}`}</span>
                          <span className="tabular-nums">
                            {formater(l, { day: 'numeric', month: 'short' })}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * Les deux fenêtres « Récupérer », une par bouton comme dans l'original.
 *
 * La progression se lit en entier ; le programme, long, a sa recherche. Un clic
 * ajoute l'élément et referme la fenêtre.
 */
function DialogueRecuperation({
  recuperation, surFermeture, surChoix,
}: {
  recuperation: Recuperation | null;
  surFermeture: () => void;
  surChoix: (element: ElementRecuperable) => void;
}) {
  const [recherche, setRecherche] = useState('');
  useEffect(() => { setRecherche(''); }, [recuperation]);

  const programme = recuperation?.source === 'programme';
  const termes = normaliser(recherche).split(/\s+/).filter(Boolean);
  const visibles = (recuperation?.elements ?? []).filter((e) => {
    if (!programme || termes.length === 0) return true;
    const texte = normaliser(`${e.etiquette} ${e.titre} ${e.contenu}`);
    return termes.every((t) => texte.includes(t));
  });

  return (
    <Dialogue
      titre={recuperation?.titre ?? ''}
      ouvert={recuperation !== null}
      surFermeture={surFermeture}
      largeur="lg"
    >
      <div className="flex min-h-0 flex-col gap-3">
        {programme && (
          <Champ
            libelle="Rechercher dans le programme"
            libelleMasque
            type="search"
            placeholder="Rechercher dans le programme…"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            icone={<IconeRecherche taille={16} />}
            autoFocus
          />
        )}
        <p className="text-[13px] text-[var(--color-fg-muted)]">
          {programme
            ? 'Contenu officiel du programme pour ce domaine et votre classe. Cliquez pour l’ajouter (modifiable ensuite).'
            : 'Cliquez sur une bulle pour l’ajouter (titre de la séance + contenu de la bulle). Le texte reste modifiable ensuite.'}
          {programme && termes.length > 0 && (
            <strong>{` · ${visibles.length} résultat${visibles.length > 1 ? 's' : ''}`}</strong>
          )}
        </p>

        <ul className={`flex flex-col gap-1 ${classeListeDialogue}`}>
          {visibles.map((e) => (
            <li key={e.cle}>
              <button
                type="button"
                onClick={() => surChoix(e)}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-left text-[13px] leading-snug hover:border-[var(--color-primary)] hover:bg-[var(--color-surface-hover)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
              >
                {e.pastille && (
                  <span className="mb-1 inline-block rounded bg-[var(--color-surface-raised)] px-1.5 py-0.5 text-[11px] text-[var(--color-fg-muted)]">
                    {e.pastille}
                  </span>
                )}
                {e.etiquette && (
                  <span className="block text-[11px] font-bold text-[var(--color-primary)]">{e.etiquette}</span>
                )}
                {e.titre && <span className="block font-semibold">{e.titre}</span>}
                {e.contenu && (
                  <span className="block whitespace-pre-wrap text-[var(--color-fg-muted)]">{e.contenu}</span>
                )}
              </button>
            </li>
          ))}
        </ul>

        <ActionsDialogue>
          <Bouton variante="secondary" onClick={surFermeture}>Fermer</Bouton>
        </ActionsDialogue>
      </div>
    </Dialogue>
  );
}

/** Une copie future retouchée depuis la duplication (`/duplicate/futures`). */
interface CopieRetouchee {
  id: string;
  date: string;
  startsAt: string | null;
  subjectName: string;
  preview: string;
}

/**
 * Décocher « Duplication » quand des copies à venir ont été retouchées.
 *
 * Reprend `_confirmerAnnulationDuplication` de l'original : la liste des
 * séances retouchées, RIEN de coché au départ, et l'on coche celles à
 * garder. Ne rien cocher revient à tout supprimer, mais après l'avoir vu et
 * dit. Fermer sans confirmer ne change rien : la case reste cochée.
 */
function DialogueAnnulationDuplication({
  annulation, surFermeture, surConfirmation,
}: {
  annulation: { seanceId: string; copies: CopieRetouchee[] } | null;
  surFermeture: () => void;
  surConfirmation: (garder: string[]) => Promise<void>;
}) {
  const [garder, setGarder] = useState<Set<string>>(new Set());
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => { setGarder(new Set()); }, [annulation]);

  const n = annulation?.copies.length ?? 0;
  return (
    <Dialogue
      titre="Annuler la duplication"
      ouvert={annulation !== null}
      surFermeture={surFermeture}
      largeur="lg"
    >
      <div className="flex min-h-0 flex-col gap-3">
        <p className="text-[13px]">
          {n > 1
            ? `${n} séances des semaines à venir ont été modifiées depuis la duplication. `
            : 'Une séance des semaines à venir a été modifiée depuis la duplication. '}
          Cochez celles que vous voulez <strong>garder</strong>. Les autres seront supprimées.
        </p>
        <ul className={`flex flex-col gap-2 ${classeListeDialogue}`}>
          {(annulation?.copies ?? []).map((c) => (
            <li key={c.id}>
              <CaseACocher
                libelle={[
                  formater(c.date, { weekday: 'long', day: 'numeric', month: 'long' }),
                  c.startsAt,
                  c.subjectName,
                ].filter(Boolean).join(' · ')}
                {...(c.preview ? { description: c.preview } : {})}
                checked={garder.has(c.id)}
                onChange={(e) => {
                  const coche = e.target.checked;
                  setGarder((g) => {
                    const suite = new Set(g);
                    if (coche) suite.add(c.id); else suite.delete(c.id);
                    return suite;
                  });
                }}
              />
            </li>
          ))}
        </ul>
        <p className="text-[12px] text-[var(--color-fg-muted)]">
          Une séance gardée devient une séance ordinaire : elle ne suivra plus la duplication.
        </p>
        <ActionsDialogue>
          <Bouton variante="ghost" onClick={surFermeture}>Ne rien changer</Bouton>
          <Bouton
            chargement={envoi}
            onClick={() => {
              setEnvoi(true);
              void surConfirmation([...garder]).finally(() => setEnvoi(false));
            }}
          >
            Confirmer
          </Bouton>
        </ActionsDialogue>
      </div>
    </Dialogue>
  );
}
